/**
 * Autosave (`061-editor.md` §8) and the draft write (`112-offline-drafts.md` §4).
 *
 * **There are TWO independent persistence mechanisms, and conflating them is the mistake
 * to avoid.**
 *
 * | | Local draft | Network save |
 * |---|---|---|
 * | Target | IndexedDB | the outbox → `PATCH /api/pages/{id}` |
 * | Debounce | **~500ms** | **~800ms** |
 * | Can it fail? | Essentially no | Yes — offline, 412, 500, anything |
 * | Purpose | *Never lose data* | Make the data real |
 *
 * The draft write always happens, and it happens FIRST. It is not conditional on the
 * network save, it does not wait for it, and it does not care whether it succeeds. If the
 * tab crashes, the browser is killed or the machine loses power, the draft is what
 * survives.
 *
 * **An implementer who "simplifies" this into one debounced save has deleted the
 * project's first design value. Do not.**
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { UNCONFIRMED, type ProseMirrorDoc } from '@/api/endpoints/pages';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { stripTransientAttrs } from '@/lib/content';
import { hasPendingUploads } from '@/offline/db';
import { saveDraft } from '@/offline/drafts';
import { outbox } from '@/offline/outbox';

const DRAFT_MS = 500;
const SAVE_MS = 800;

export interface AutosaveOptions {
  pageId: string;
  title: string;
  /** The `updatedAt` the editor last SAW from the server — the save's precondition. */
  baseUpdatedAt: string;
  /** Set while a conflict banner is open: the editor is read-only and must not save. */
  frozen: boolean;
  /** The document the editor was MOUNTED with — the baseline "nothing has changed yet". */
  initialContent: ProseMirrorDoc;
}

export interface Autosave {
  /** Call from Tiptap's `onUpdate`. */
  onChange: (doc: ProseMirrorDoc) => void;
  /** Cancel the debounces and write both, now. Blur, tab-hide, `Mod+S`, route change. */
  flush: () => void;
  /**
   * Save immediately, skipping the debounce entirely.
   *
   * **The upload manager calls this when the last in-flight upload resolves** (`062` §3
   * step 7, mechanism 2 — "the half of the requirement everyone forgets"). Without it, a
   * user who pastes an image and then stops typing would never save the page: the 800 ms
   * debounce was suppressed by `hasPendingUploads` while the upload was in flight, and
   * nothing would ever re-arm it.
   */
  saveNow: (doc: ProseMirrorDoc) => void;
  /** True while a debounce is armed or a write is queued but not yet delivered. */
  isDirty: () => boolean;
}

export function useAutosave({
  pageId,
  title,
  baseUpdatedAt,
  frozen,
  initialContent,
}: AutosaveOptions): Autosave {
  // Refs, not state: these feed a debounced callback and must not restart its timer, and
  // a re-render on every keystroke is a real defect in an app that must feel instant.
  const titleRef = useRef(title);
  const baseRef = useRef(baseUpdatedAt);
  const frozenRef = useRef(frozen);

  /**
   * SEEDED with the mounted document, not `null`.
   *
   * `061` §8: "A page whose content did not actually change does not fire a PATCH. A cursor
   * move is not an edit." Starting this at `null` means the first `onUpdate` Tiptap emits —
   * which it does for its own schema normalisation on load, before the user has typed a
   * single character — compares against nothing, looks like a change, and enqueues a save of
   * a document identical to the one the server already has.
   *
   * That save is not merely wasteful. It carries `If-Unmodified-Since`, and it races the
   * title save the tree queued moments earlier: the title lands, bumps `updated_at`, and the
   * pointless content save then comes back **412** — opening a recovery banner over a
   * brand-new page that nobody else has ever touched.
   */
  const lastSaved = useRef<string>(JSON.stringify(stripTransientAttrs(initialContent)));
  const latestDoc = useRef<ProseMirrorDoc | null>(null);

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  useEffect(() => {
    // The precondition advances only when the SERVER tells us it has (the PATCH response,
    // applied to the detail cache by `OutboxBridge`). We never invent it.
    baseRef.current = baseUpdatedAt;
  }, [baseUpdatedAt]);

  useEffect(() => {
    frozenRef.current = frozen;
  }, [frozen]);

  /* ── The draft: fast, local, cannot fail ─────────────────────────────────── */

  const writeDraft = useDebouncedCallback((doc: ProseMirrorDoc) => {
    void saveDraft({
      pageId,
      content: doc,
      title: titleRef.current,
      baseUpdatedAt: baseRef.current,
      savedAt: Date.now(),
    });
  }, DRAFT_MS);

  /* ── The network save: slower, remote, may queue ─────────────────────────── */

  const pushToServer = useCallback(
    (doc: ProseMirrorDoc) => {
      void (async () => {
        // §8 layer 1 — HARD BLOCK. A page must NEVER be persisted with a `blob:` src. When
        // the last upload for the page resolves, the upload manager calls `saveNow` and we
        // come straight back through here with the gate open (`062` §3 step 7).
        if (await hasPendingUploads(pageId)) return;

        const content = stripTransientAttrs(doc);
        const serialized = JSON.stringify(content);

        // A cursor move is not an edit, and neither is Tiptap normalising the document it
        // was just handed (`061` §8).
        if (serialized === lastSaved.current) return;
        lastSaved.current = serialized;

        const base = baseRef.current;

        await outbox.enqueue({
          kind: 'page.update',
          entityId: pageId,
          label: titleRef.current,
          request: {
            method: 'PATCH',
            path: `/api/pages/${pageId}`,
            // Only `content`. Never `title` (that is the tree's PATCH), never `contentText`
            // — the server owns that projection and ignores any we send (`031`).
            body: { content },
            // Sent whenever we HAVE a server version to be stale against. `040` makes it
            // optional for a live editor; we send it anyway, because the cost is one header
            // and the benefit is that a second tab, an outbox replay, or a PATCH from
            // elsewhere cannot silently clobber the body of the page.
            //
            // The one case where it is omitted is a page the server has not acknowledged
            // yet (`UNCONFIRMED`): there is no version to be stale against, nothing can
            // have raced us, and inventing a timestamp would 412 every brand-new page.
            ...(base === UNCONFIRMED ? {} : { ifUnmodifiedSince: base }),
          },
        });
      })();
    },
    [pageId],
  );

  const writeServer = useDebouncedCallback(pushToServer, SAVE_MS);

  const onChange = useCallback(
    (doc: ProseMirrorDoc) => {
      if (frozenRef.current) return; // a conflict is open; the editor is read-only
      latestDoc.current = doc;
      writeDraft(doc); // fast, local, cannot fail
      writeServer(doc); // slower, remote, may queue
    },
    [writeDraft, writeServer],
  );

  const flush = useCallback(() => {
    writeDraft.flush();
    writeServer.flush();
  }, [writeDraft, writeServer]);

  const saveNow = useCallback(
    (doc: ProseMirrorDoc) => {
      if (frozenRef.current) return;
      latestDoc.current = doc;
      // The draft first, always — it is the one that cannot fail (`112` §4).
      writeDraft(doc);
      writeDraft.flush();
      // The debounced network save is cancelled rather than left armed: it would otherwise
      // fire again in 800ms with the same document and be discarded by the `lastSaved`
      // check, which is harmless but is a lie in the sync indicator for no reason.
      writeServer.cancel();
      pushToServer(doc);
    },
    [pushToServer, writeDraft, writeServer],
  );

  const isDirty = useCallback(
    () => writeDraft.pending() || writeServer.pending(),
    [writeDraft, writeServer],
  );

  /* ── The other triggers (`061` §8, `112` §4) ─────────────────────────────── */

  useEffect(() => {
    const onVisibility = (): void => {
      // Tab-switching is the single most common way people leave a page, and unlike
      // `beforeunload` it is a reliable, awaitable moment. Flush BOTH.
      if (document.visibilityState === 'hidden') flush();
    };

    const onBeforeUnload = (): void => {
      // IndexedDB writes are async and `beforeunload` cannot await — but the transaction
      // is STARTED, and browsers overwhelmingly complete an already-started IDB
      // transaction during unload. The 500ms debounce means at most half a second is at
      // risk anyway. The draft is the guarantee; the network save here is not.
      flush();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      // People press it. A no-op — or worse, the browser's "Save page" dialog — feels
      // broken, so it does what the app was going to do anyway, immediately.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        flush();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeyDown);
      // Navigating away from a page you have just typed into must not lose the last
      // sentence. The debounced callbacks flush themselves on unmount too, but say it
      // here as well — this is the path the user actually takes.
      flush();
    };
  }, [flush]);

  return { onChange, flush, saveNow, isDirty };
}

/** Wire an editor's blur to an immediate flush (`061` §8). */
export function useFlushOnBlur(editor: Editor | null, flush: () => void): void {
  useEffect(() => {
    if (editor === null) return;
    editor.on('blur', flush);
    return () => {
      editor.off('blur', flush);
    };
  }, [editor, flush]);
}
